import { Language, TranslateStore } from '@ngx-translate/core';
import { inject, PLATFORM_ID, Provider, Renderer2, RendererFactory2 } from '@angular/core';
import { StorageService } from '@coin/shared/core/data-access';
import { StorageKey } from '@coin/shared/core/util-enums';
import { isPlatformBrowser } from '@angular/common';
import { ProdAdmin } from '@coin/shared/core/util-helpers';

export function provideTranslateDebugStore(): Provider {
  return { provide: TranslateStore, useClass: TranslateDebugStore };
}

const ONE_SECOND_IN_MS = 1000;

class TranslateDebugStore extends TranslateStore {
  private storageService = inject(StorageService);

  private readonly platform = inject(PLATFORM_ID);
  private debug: boolean;
  private renderer: Renderer2;
  private ttlCache: Map<string, Date> = new Map();
  private translationComponent: 'TranslationsCustomerComponent' | 'TranslationsAdminComponent';

  constructor() {
    const rendererFactory = inject(RendererFactory2);

    super();
    this.debug = this.storageService.get(StorageKey.TRANSLATIONS_DEBUG_MODE_ENABLED) === 'true';
    this.renderer = rendererFactory.createRenderer(null, null);

    if (isPlatformBrowser(this.platform)) {
      this.translationComponent = window.location.host.includes('admin')
        ? 'TranslationsAdminComponent'
        : 'TranslationsCustomerComponent';
    }
  }

  public getValue(language: Language, key: string) {
    if (this.debug) {
      this.makeMatchingElementsEditable(key);
      return key;
    }

    return super.getValue(language, key);
  }

  private makeMatchingElementsEditable(key: string): void {
    const now = new Date();
    const firstSeenAt = this.ttlCache.get(key);
    const lastSeenLongerThanOneSecondAgo = now.getTime() - (firstSeenAt?.getTime() ?? 0) > ONE_SECOND_IN_MS;
    const shouldCheckElement = lastSeenLongerThanOneSecondAgo;

    if (shouldCheckElement) {
      this.ttlCache.set(key, new Date());

      setTimeout(() => {
        const xpath = `//*[contains(text(),'${key}')]`;
        const matchingElements = document.evaluate(xpath, document, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);

        this.ttlCache.set(key, now);
        for (let i = 0; i < matchingElements.snapshotLength; i++) {
          const matchingElement = matchingElements.snapshotItem(i);

          if (!matchingElement?.parentElement.getAttribute('custom-translate-seen')) {
            const button = this.createButton();

            this.renderer.listen(button, 'click', event => {
              event.stopPropagation();
              ProdAdmin.open(this.translationComponent, { queryParams: { key } });
            });

            let textChild;
            matchingElement?.childNodes.forEach(childNode => {
              if (childNode.nodeType === Node.TEXT_NODE) {
                textChild = childNode;
              }
            });

            if (textChild) {
              try {
                this.renderer.setStyle(matchingElement, 'display', 'flex');
                this.renderer.setStyle(matchingElement, 'align-items', 'center');
                this.renderer.insertBefore(matchingElement, button, textChild);
                this.renderer.setAttribute(matchingElement.parentElement, 'custom-translate-seen', 'true');
              } catch (e) {
                // Do nothing
              }
            }
          }
        }
      }, 250);
    }
  }

  private createButton(): Node {
    const button = this.renderer.createElement('button');
    this.renderer.setStyle(button, 'margin-right', '5px');
    this.renderer.setStyle(button, 'border-radius', '12px');
    this.renderer.setStyle(button, 'padding', '2px');
    this.renderer.setStyle(button, 'border-color', 'var(--purple700)');
    this.renderer.setStyle(button, 'background-color', 'var(--purple500)');
    this.renderer.setStyle(button, 'color', 'white');
    this.renderer.setStyle(button, 'display', 'flex');
    this.renderer.setStyle(button, 'align-items', 'center');
    this.renderer.setStyle(button, 'justify-content', 'center');
    button.innerHTML = '<i class="material-icons" style="width: 14px; height: 14px; font-size: 14px;">edit</i>';

    return button;
  }
}
